// Hook: fetch the real HubSpot contact profile for a route-level
// customerId. Backs the W25 identity-row fix so the Current Action
// screen stops showing the Sarah Mitchell design fixture regardless of
// which contact the HubSpot card opened the iframe from.
//
// Shape:
//   const profile = useContactProfile(customerId)
//
//   profile.status === 'loading' | 'ready' | 'empty' | 'error' | 'skipped'
//   profile.contact: { id, firstName, lastName, fullName, initials, email,
//                       phone, city, state, country, ... } | null
//
// Status 'skipped' means we're in preview / dev-mock mode (no session or
// explicit ?preview=) and the screen should keep the illustrative
// fixture (SARAH).
//
// Implementation note (W25.1): the hook does NOT go through
// useScreenRuntime because that helper insists on a non-empty
// VITE_RUNTIME_API_BASE_URL and the sales-workspace container is
// deployed without one (nginx inside the same image proxies /v1/ to
// the api-server). Instead we issue a RELATIVE fetch against the
// same origin and attach the session headers the W24 verifier bypass
// requires (Authorization + X-Customer-Id + X-Portal-Id + optional
// X-User-Id / X-User-Email on url-bootstrap sessions). This mirrors
// the browser contract documented in apps/sales-workspace/src/api/
// transport.ts.
//
// W26 (2026-10-20): the session gate + header builder are exported so
// the timeline / horses hooks (useContactCollections.ts) share one
// definition of "is this a real session" and one header contract.

import { useEffect, useRef, useState } from 'react';

import { isDevMockSession } from '../../auth/mockContext.js';
import type { HubSpotSession } from '../../auth/hubspot-handshake.js';
import { useOptionalSession } from '../../auth/session-context.js';
import { usePreviewFixtureMode } from './useAuthenticatedCustomer.js';

export interface ContactProfileRecord {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  country: string;
  createdAt: string | null;
  leadStatus: string | null;
  lifecycleStage: string | null;
  lastCallDate: string | null;
  vsNotes: string | null;
  // W26 additions. Optional so a pre-W26 api-server payload still
  // type-checks; every consumer treats undefined as null.
  lastCommentVs?: string | null;
  callPriorityVs?: string | null;
  leadStatusVs?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  // W38 (2026-10-20): HubSpot `preferred_channel` (Text / Email / Call /
  // No Preference / Do Not Contact) or null when unset. Optional so a
  // pre-W38 api-server payload still type-checks.
  preferredChannel?: string | null;
}

export type ContactProfileStatus =
  | 'loading'
  | 'ready'
  | 'empty'
  | 'error'
  | 'skipped';

export interface ContactProfileResult {
  status: ContactProfileStatus;
  contact: ContactProfileRecord | null;
  error?: string;
  errorCode?: string;
}

/**
 * The headers every same-origin /v1 read attaches. Mirrors transport.ts
 * and the api-server url-bootstrap verifier (middleware/hubspot-session).
 */
export function buildSessionHeaders(session: HubSpotSession): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${session.token}`,
    'X-Customer-Id': session.context.objectId,
    'X-Portal-Id': String(session.portalId),
  };
  if (session.work) headers['X-Work-Id'] = session.work.workId;
  if (session.urlBootstrapped) {
    if (session.userId) headers['X-User-Id'] = session.userId;
    if (session.email) headers['X-User-Email'] = session.email;
  }
  return headers;
}

export interface ContactReadGate {
  /** True when a real (non-preview, non-dev-mock) session can fetch. */
  canFetch: boolean;
  session: HubSpotSession | null;
  /** Stable identity + token keys for effect deps. */
  sessionIdentity: string | null;
  sessionToken: string | null;
}

/**
 * Shared "may this screen talk to the api-server for this customer"
 * decision. Preview (?preview=sarah) and the dev mock session always
 * answer no so the design fixture is never mixed with real reads.
 */
export function useContactReadGate(customerId: string | null | undefined): ContactReadGate {
  const sessionCtx = useOptionalSession();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(sessionCtx?.session ?? null);
  const sessionReady =
    !!sessionCtx &&
    (sessionCtx.status === 'ready' || sessionCtx.status === 'refreshing') &&
    !!sessionCtx.session;
  const canFetch = !!customerId && sessionReady && !previewFlag && !devMock;
  const session = sessionCtx?.session ?? null;
  return {
    canFetch,
    session,
    sessionIdentity: session ? `${session.portalId}:${session.userId}` : null,
    sessionToken: session?.token ?? null,
  };
}

export function useContactProfile(
  customerId: string | null | undefined,
): ContactProfileResult {
  const gate = useContactReadGate(customerId);
  const { canFetch, sessionIdentity, sessionToken } = gate;

  const [state, setState] = useState<ContactProfileResult>(() => ({
    status: canFetch ? 'loading' : 'skipped',
    contact: null,
  }));
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!canFetch || !customerId) {
      setState({ status: 'skipped', contact: null });
      return;
    }
    const session = gate.session;
    if (!session) {
      setState({ status: 'skipped', contact: null });
      return;
    }
    const requestId = `${customerId}:${Date.now()}`;
    latest.current = requestId;
    setState({ status: 'loading', contact: null });

    const headers = buildSessionHeaders(session);

    const url = `/v1/contacts/${encodeURIComponent(customerId)}`;
    fetch(url, { method: 'GET', headers })
      .then(async (response) => {
        if (latest.current !== requestId) return;
        if (response.status === 404) {
          setState({ status: 'empty', contact: null });
          return;
        }
        if (!response.ok) {
          let errorCode = `HTTP_${response.status}`;
          try {
            const bodyText = await response.text();
            if (bodyText) {
              try {
                const parsed = JSON.parse(bodyText) as { errorCode?: string };
                if (parsed && typeof parsed.errorCode === 'string') {
                  errorCode = parsed.errorCode;
                }
              } catch {
                /* non-JSON error body */
              }
            }
          } catch {
            /* ignore */
          }
          setState({
            status: 'error',
            contact: null,
            error: `Contact fetch failed with status ${response.status}`,
            errorCode,
          });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          setState({
            status: 'error',
            contact: null,
            error: 'Contact response was not valid JSON',
            errorCode: 'BAD_JSON',
          });
          return;
        }
        if (
          !body ||
          typeof body !== 'object' ||
          !(body as { profile?: ContactProfileRecord | null }).profile
        ) {
          setState({ status: 'empty', contact: null });
          return;
        }
        setState({
          status: 'ready',
          contact: (body as { profile: ContactProfileRecord }).profile,
        });
      })
      .catch((err: unknown) => {
        if (latest.current !== requestId) return;
        setState({
          status: 'error',
          contact: null,
          error: err instanceof Error ? err.message : 'Unknown fetch error',
          errorCode: 'FETCH_FAILED',
        });
      });
    return () => {
      if (latest.current === requestId) latest.current = null;
    };
    // sessionIdentity + sessionToken capture token refreshes without
    // closing over the full session object (which would re-run on every
    // emit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, customerId, sessionIdentity, sessionToken]);

  return state;
}
